import type { ArabicDialect } from "@repo/agent-config";
import type { ChatLabel } from "@repo/embed";

/**
 * The chat's own wording in the languages of the markets it serves, picked from the app's locale
 * (the `locale` param, the launch token or the `locale` message) and else the device's. A
 * retailer's or app's own wording (settings, `text`) always wins over these. The live call screen
 * keeps English.
 */
export const COPY_LANGUAGES = ["en", "ar", "fr"] as const;
export type CopyLanguage = (typeof COPY_LANGUAGES)[number];

/** `ar-AE` -> `ar`; anything else we have no wording for -> English. */
export function copyLanguage(locale: string | null | undefined): CopyLanguage {
	const base = locale?.toLowerCase().split("-")[0];
	return COPY_LANGUAGES.find((language) => language === base) ?? "en";
}

export interface Copy {
	welcomeTitle: string;
	/** `{name}`: the assistant's name. */
	placeholder: string;
	labels: Record<ChatLabel, string>;
	poweredBy: string;
	privacy: string;
	noAnswer: string;
	/** The button that stops a reply while it is coming. */
	stop: string;
	/** A reply the shopper stopped before it said anything. */
	stopped: string;
	teamJoining: string;
	/** `{retailer}` */
	teamWillReply: string;
	/** `{retailer}` */
	launchRequired: string;
	newChatTitle: string;
	newChatBody: string;
	keepChatting: string;
	startOver: string;
	voiceBetaTitle: string;
	voiceBetaBody: string;
	notNow: string;
	startTalking: string;
	back: string;
	closeChat: string;
	more: string;
	items: (count: number) => string;
	empty: string;
	inStock: string;
	outOfStock: string;
	offer: string;
	/** `{percent}` */
	percentOff: string;
	similar: string;
	findSimilar: string;
	/** `{title}`: what "Find something similar" sends. */
	alternativesTo: string;
	allInBasket: (count: number) => string;
	addAll: (count: number) => string;
	addOthers: (count: number) => string;
	transcribing: string;
	openingMic: string;
	letGoToSend: string;
	steps: {
		searching: string;
		/** `{query}` */
		searchingFor: string;
		/** `{count}` */
		searchingProducts: string;
		deals: string;
		noDeals: string;
		noMatches: string;
		/** `{query}` */
		noMatchesFor: string;
		/** `{count}` */
		matches: (count: number) => string;
		recipe: string;
		recipeReady: string;
		allIngredients: string;
		/** `{found}`, `{total}` */
		someIngredients: string;
		product: string;
		productChecked: string;
		adding: string;
		basketUpdated: string;
		updating: string;
		emptying: string;
		emptied: string;
		checkingBasket: string;
		basketChecked: string;
		substitution: string;
		substitutionNoted: string;
		order: string;
		orderChecked: string;
		team: string;
		teamTold: string;
		answer: string;
		answerNoted: string;
		remembering: string;
		remembered: string;
		forgetting: string;
		forgotten: string;
		working: string;
		done: string;
		failed: string;
	};
}

export const COPY: Record<CopyLanguage, Copy> = {
	en: {
		welcomeTitle: "What are you shopping for today?",
		placeholder: "Ask {name}…",
		labels: {
			viewBasket: "View basket",
			basketTitle: "Your basket",
			checkout: "Check out with {retailer}",
			addToBasket: "Add to basket",
			orderHelp: "Help with an order",
			newChat: "Start a new chat",
			total: "Total",
			emptyBasket: "Nothing here yet",
			thinking: "Thinking",
			typing: "Typing",
			tryAgain: "Try again",
		},
		poweredBy: "Powered by",
		privacy: "Privacy",
		noAnswer: "That answer didn't come through. Your message is saved.",
		stop: "Stop",
		stopped: "You stopped this answer.",
		teamJoining: "A team member is joining",
		teamWillReply: "Someone from {retailer} will reply here.",
		launchRequired: "This chat opens from the {retailer} app",
		newChatTitle: "Start a new conversation?",
		newChatBody: "This clears the chat and empties your basket.",
		keepChatting: "Keep chatting",
		startOver: "Start over",
		voiceBetaTitle: "Voice is in beta",
		voiceBetaBody:
			"It may mishear you or take a moment to answer. You can always type instead.",
		notNow: "Not now",
		startTalking: "Start talking",
		back: "Back",
		closeChat: "Close chat",
		more: "More",
		items: (n) => (n === 1 ? "1 item" : `${n} items`),
		empty: "Empty",
		inStock: "In stock",
		outOfStock: "Out of stock",
		offer: "Offer",
		percentOff: "{percent}% off",
		similar: "Similar",
		findSimilar: "Find something similar",
		alternativesTo: "Show me alternatives to {title}",
		allInBasket: (n) => `All ${n} in your basket`,
		addAll: (n) => `Add all ${n}`,
		addOthers: (n) => `Add the other ${n}`,
		transcribing: "Transcribing…",
		openingMic: "Opening the mic…",
		letGoToSend: "Let go to send",
		steps: {
			searching: "Searching the store",
			searchingFor: "Searching for {query}",
			searchingProducts: "Searching for {count} products",
			deals: "Looking for today's deals",
			noDeals: "No deals right now",
			noMatches: "No matches",
			noMatchesFor: "No matches for {query}",
			matches: (n) => (n === 1 ? "1 match" : `${n} matches`),
			recipe: "Writing the recipe",
			recipeReady: "Recipe ready",
			allIngredients: "Found every ingredient",
			someIngredients: "Found {found} of {total} ingredients",
			product: "Checking the product",
			productChecked: "Checked the product",
			adding: "Adding to your basket",
			basketUpdated: "Updated your basket",
			updating: "Updating your basket",
			emptying: "Emptying your basket",
			emptied: "Emptied your basket",
			checkingBasket: "Checking your basket",
			basketChecked: "Checked your basket",
			substitution: "Noting your replacement choice",
			substitutionNoted: "Noted your replacement choice",
			order: "Checking your order",
			orderChecked: "Checked your order",
			team: "Contacting the team",
			teamTold: "The team has been told",
			answer: "Noting your answer",
			answerNoted: "Noted your answer",
			remembering: "Remembering that",
			remembered: "Remembered for next time",
			forgetting: "Forgetting that",
			forgotten: "Forgotten",
			working: "Working on it",
			done: "Done",
			failed: "That step didn't work",
		},
	},
	ar: {
		welcomeTitle: "شو بدك تشتري اليوم؟",
		placeholder: "اسأل {name}…",
		labels: {
			viewBasket: "عرض السلة",
			basketTitle: "سلتك",
			checkout: "إتمام الطلب مع {retailer}",
			addToBasket: "أضف إلى السلة",
			orderHelp: "مساعدة بطلب",
			newChat: "محادثة جديدة",
			total: "المجموع",
			emptyBasket: "السلة فارغة",
			thinking: "عم فكّر",
			typing: "عم اكتب",
			tryAgain: "حاول مرة ثانية",
		},
		poweredBy: "بدعم من",
		privacy: "الخصوصية",
		noAnswer: "ما وصل الجواب. رسالتك محفوظة.",
		stop: "وقّف",
		stopped: "وقّفت هالجواب.",
		teamJoining: "حدا من الفريق عم ينضم",
		teamWillReply: "حدا من {retailer} رح يرد عليك هون.",
		launchRequired: "هالمحادثة بتنفتح من تطبيق {retailer}",
		newChatTitle: "بدك تبلّش محادثة جديدة؟",
		newChatBody: "رح تنمسح المحادثة وتفضى سلتك.",
		keepChatting: "كمّل المحادثة",
		startOver: "ابدأ من جديد",
		voiceBetaTitle: "الصوت بعده تجريبي",
		voiceBetaBody:
			"ممكن ما يفهمك منيح أو ياخد لحظة ليرد. فيك دايماً تكتب بدلها.",
		notNow: "مش هلق",
		startTalking: "ابدأ الحكي",
		back: "رجوع",
		closeChat: "سكّر المحادثة",
		more: "المزيد",
		items: (n) => (n === 1 ? "غرض واحد" : `${n} أغراض`),
		empty: "فارغة",
		inStock: "متوفر",
		outOfStock: "غير متوفر",
		offer: "عرض",
		percentOff: "خصم {percent}٪",
		similar: "مشابه",
		findSimilar: "لاقيلي شي مشابه",
		alternativesTo: "فرجيني بدائل لـ {title}",
		allInBasket: (n) => `كل الـ ${n} بسلتك`,
		addAll: (n) => `أضف الكل (${n})`,
		addOthers: (n) => `أضف الباقي (${n})`,
		transcribing: "عم حوّل الصوت لنص…",
		openingMic: "عم افتح الميكروفون…",
		letGoToSend: "شيل إصبعك لتبعت",
		steps: {
			searching: "عم دوّر بالمحل",
			searchingFor: "عم دوّر على {query}",
			searchingProducts: "عم دوّر على {count} منتجات",
			deals: "عم دوّر على عروض اليوم",
			noDeals: "ما في عروض هلق",
			noMatches: "ما لقيت شي",
			noMatchesFor: "ما لقيت شي لـ {query}",
			matches: (n) => (n === 1 ? "نتيجة وحدة" : `${n} نتائج`),
			recipe: "عم اكتب الوصفة",
			recipeReady: "الوصفة جاهزة",
			allIngredients: "لقيت كل المكونات",
			someIngredients: "لقيت {found} من {total} مكونات",
			product: "عم شيّك على المنتج",
			productChecked: "شيّكت على المنتج",
			adding: "عم ضيف على سلتك",
			basketUpdated: "حدّثت سلتك",
			updating: "عم حدّث سلتك",
			emptying: "عم فضّي سلتك",
			emptied: "فضّيت سلتك",
			checkingBasket: "عم شيّك على سلتك",
			basketChecked: "شيّكت على سلتك",
			substitution: "عم سجّل خيار البديل",
			substitutionNoted: "سجّلت خيار البديل",
			order: "عم شيّك على طلبك",
			orderChecked: "شيّكت على طلبك",
			team: "عم تواصل مع الفريق",
			teamTold: "الفريق صار عنده خبر",
			answer: "عم سجّل جوابك",
			answerNoted: "سجّلت جوابك",
			remembering: "عم احفظ هالشي",
			remembered: "انحفظ للمرة الجاية",
			forgetting: "عم انسى هالشي",
			forgotten: "انتسى",
			working: "عم اشتغل عليه",
			done: "خلص",
			failed: "هالخطوة ما زبطت",
		},
	},
	fr: {
		welcomeTitle: "Que cherchez-vous aujourd'hui ?",
		placeholder: "Demandez à {name}…",
		labels: {
			viewBasket: "Voir le panier",
			basketTitle: "Votre panier",
			checkout: "Commander avec {retailer}",
			addToBasket: "Ajouter au panier",
			orderHelp: "Aide pour une commande",
			newChat: "Nouvelle conversation",
			total: "Total",
			emptyBasket: "Rien pour l'instant",
			thinking: "Réflexion",
			typing: "Écrit",
			tryAgain: "Réessayer",
		},
		poweredBy: "Propulsé par",
		privacy: "Confidentialité",
		noAnswer: "La réponse n'est pas arrivée. Votre message est enregistré.",
		stop: "Arrêter",
		stopped: "Vous avez arrêté cette réponse.",
		teamJoining: "Un membre de l'équipe arrive",
		teamWillReply: "Quelqu'un de {retailer} vous répondra ici.",
		launchRequired:
			"Cette conversation s'ouvre depuis l'application {retailer}",
		newChatTitle: "Commencer une nouvelle conversation ?",
		newChatBody: "La conversation sera effacée et votre panier vidé.",
		keepChatting: "Continuer",
		startOver: "Recommencer",
		voiceBetaTitle: "La voix est en version bêta",
		voiceBetaBody:
			"Elle peut mal vous comprendre ou mettre un moment à répondre. Vous pouvez toujours écrire.",
		notNow: "Pas maintenant",
		startTalking: "Parler",
		back: "Retour",
		closeChat: "Fermer",
		more: "Plus",
		items: (n) => (n === 1 ? "1 article" : `${n} articles`),
		empty: "Vide",
		inStock: "En stock",
		outOfStock: "En rupture",
		offer: "Promo",
		percentOff: "-{percent} %",
		similar: "Similaire",
		findSimilar: "Trouver un produit similaire",
		alternativesTo: "Montre-moi des alternatives à {title}",
		allInBasket: (n) => `Les ${n} sont dans votre panier`,
		addAll: (n) => `Tout ajouter (${n})`,
		addOthers: (n) => `Ajouter les ${n} autres`,
		transcribing: "Transcription…",
		openingMic: "Ouverture du micro…",
		letGoToSend: "Relâchez pour envoyer",
		steps: {
			searching: "Recherche dans le magasin",
			searchingFor: "Recherche de {query}",
			searchingProducts: "Recherche de {count} produits",
			deals: "Recherche des promos du jour",
			noDeals: "Pas de promo en ce moment",
			noMatches: "Aucun résultat",
			noMatchesFor: "Aucun résultat pour {query}",
			matches: (n) => (n === 1 ? "1 résultat" : `${n} résultats`),
			recipe: "Rédaction de la recette",
			recipeReady: "Recette prête",
			allIngredients: "Tous les ingrédients trouvés",
			someIngredients: "{found} ingrédients sur {total} trouvés",
			product: "Vérification du produit",
			productChecked: "Produit vérifié",
			adding: "Ajout au panier",
			basketUpdated: "Panier mis à jour",
			updating: "Mise à jour du panier",
			emptying: "Vidage du panier",
			emptied: "Panier vidé",
			checkingBasket: "Vérification du panier",
			basketChecked: "Panier vérifié",
			substitution: "Enregistrement du remplacement",
			substitutionNoted: "Remplacement enregistré",
			order: "Vérification de la commande",
			orderChecked: "Commande vérifiée",
			team: "Contact de l'équipe",
			teamTold: "L'équipe est prévenue",
			answer: "Enregistrement de votre réponse",
			answerNoted: "Réponse enregistrée",
			remembering: "Je m'en souviens",
			remembered: "Retenu pour la prochaine fois",
			forgetting: "J'oublie",
			forgotten: "Oublié",
			working: "En cours",
			done: "Terminé",
			failed: "Cette étape n'a pas marché",
		},
	},
};

/**
 * The Arabic wording in Gulf Arabic, for a retailer whose Arabic is not Lebanese (`arabicDialect`).
 * Only what `COPY.ar` says in Lebanese is reworded.
 */
const GULF_ARABIC: Copy = {
	...COPY.ar,
	welcomeTitle: "وش تبغى تشتري اليوم؟",
	labels: {
		...COPY.ar.labels,
		thinking: "أفكّر",
		typing: "أكتب",
	},
	noAnswer: "ما وصل الرد. رسالتك محفوظة.",
	stopped: "وقّفت هذا الرد.",
	teamJoining: "واحد من الفريق بينضم",
	teamWillReply: "واحد من {retailer} بيرد عليك هنا.",
	launchRequired: "هذي المحادثة تنفتح من تطبيق {retailer}",
	newChatTitle: "تبغى تبدأ محادثة جديدة؟",
	newChatBody: "بتنمسح المحادثة وتفضى سلتك.",
	voiceBetaTitle: "الصوت للحين تجريبي",
	voiceBetaBody:
		"ممكن ما يفهمك زين أو ياخذ لحظة عشان يرد. تقدر دايماً تكتب بداله.",
	notNow: "مو الحين",
	startTalking: "ابدأ الكلام",
	findSimilar: "دوّر لي على شي مشابه",
	alternativesTo: "ورّني بدائل لـ {title}",
	transcribing: "أحوّل الصوت لنص…",
	openingMic: "أفتح المايك…",
	letGoToSend: "شيل إصبعك عشان ترسل",
	steps: {
		...COPY.ar.steps,
		searching: "أدوّر في المتجر",
		searchingFor: "أدوّر على {query}",
		searchingProducts: "أدوّر على {count} منتجات",
		deals: "أدوّر على عروض اليوم",
		noDeals: "ما فيه عروض الحين",
		recipe: "أكتب الوصفة",
		product: "أتأكد من المنتج",
		productChecked: "تأكدت من المنتج",
		adding: "أضيف لسلتك",
		updating: "أحدّث سلتك",
		emptying: "أفضّي سلتك",
		checkingBasket: "أشيّك على سلتك",
		substitution: "أسجّل خيار البديل",
		order: "أشيّك على طلبك",
		team: "أتواصل مع الفريق",
		teamTold: "الفريق وصله خبر",
		answer: "أسجّل ردك",
		answerNoted: "سجّلت ردك",
		remembering: "أحفظ هذا",
		forgetting: "أنسى هذا",
		forgotten: "انمسح",
		working: "أشتغل عليه",
		done: "تم",
		failed: "هذي الخطوة ما ضبطت",
	},
};

/** The chat's wording for `locale`, its Arabic in the retailer's dialect. */
export function copyFor(
	locale: string | null | undefined,
	dialect: ArabicDialect,
): Copy {
	const language = copyLanguage(locale);
	return language === "ar" && dialect === "gulf"
		? GULF_ARABIC
		: COPY[language];
}

/** `{key}` placeholders filled. */
export function fill(text: string, values: Record<string, string | number>) {
	return text.replace(/\{(\w+)\}/g, (match, key: string) =>
		key in values ? String(values[key]) : match,
	);
}
